import * as THREE from "three"
import type { CameraPath, PathVec3 } from "@/lib/location-scene-3d/camera-path"
import { curvePolyline, displayedTangent, vecLen, type CurveSample } from "@/lib/location-scene-3d/camera-path-spline"
import type { SceneThemeId } from "@/lib/location-scene-3d/scene-theme"

export type PathOverlayHit = { kind: "point"; index: number } | { kind: "handle"; index: number; side: 1 | -1 }

export type PathOverlaySync = {
  path: CameraPath
  editing: boolean
  selectedIndex: number | null
  hidden: boolean
  theme: SceneThemeId
  cameraPosition: PathVec3
  icon: { pos: PathVec3; dir: PathVec3 } | null
  /** Верх сектора под точкой или null, если пола нет. */
  floors: Array<number | null>
  fov: number
  /** Верх плитки под XZ. Вершины кривой не опускаются ниже. */
  surfaceY?: (x: number, z: number) => number
  /** 1 на метровой площадке. На сантиметровой — сторона площадки / 32, не больше 1. */
  chrome?: number
  /** Место вставки под пером. null — кружок скрыт. */
  insertHover?: PathVec3 | null
  /** Чужие пути: линия без точек и без попадания. */
  ghosts?: PathVec3[][]
  /** Яркая кривая без точек, ручек и концов. */
  solid?: boolean
}

type PathColors = {
  curve: string
  handle: string
  drop: string
  frustum: string
  body: string
}

export const PATH_COLOR_TOKENS: Record<SceneThemeId, { [K in keyof PathColors]: string }> = {
  light: {
    curve: "--ui-accent",
    handle: "--text-muted",
    drop: "--text-muted",
    frustum: "--clay-9",
    body: "--clay-9",
  },
  dark: {
    curve: "--ui-accent",
    handle: "--scene-cube-night-icon",
    drop: "--scene-cube-night-icon",
    frustum: "--scene-cube-night-icon",
    body: "--scene-cube-night-icon",
  },
}

const TUBE_RADIUS = 0.05
const TUBE_SIDES = 8
const DIM_OPACITY = 0.35
const POINT_RADIUS = 0.16
const ICON_SCALE = 2
const MAX_CURVE_SEGMENT = 0.5
const MAX_DENSIFY_STEPS = 256
const FLOOR_CLEARANCE = 0.02
const DROP_DASH = 0.5
const DROP_GAP = 0.35
const DROP_HALF = 0.012
const NEAR_HIDE_M = 3

function appendDropDash(
  positions: number[],
  indices: number[],
  x: number,
  z: number,
  y0: number,
  y1: number,
  half: number,
) {
  const base = positions.length / 3
  const corners: Array<[number, number, number]> = [
    [x - half, y0, z],
    [x + half, y0, z],
    [x + half, y1, z],
    [x - half, y1, z],
    [x, y0, z - half],
    [x, y0, z + half],
    [x, y1, z + half],
    [x, y1, z - half],
  ]
  for (const corner of corners) positions.push(corner[0], corner[1], corner[2])
  for (const index of [0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7]) indices.push(base + index)
}

function appendDropDashes(
  positions: number[],
  indices: number[],
  foot: { x: number; y: number; z: number; floor: number },
  dashLen: number,
  gap: number,
  half: number,
) {
  const top = Math.max(foot.y, foot.floor)
  const bottom = Math.min(foot.y, foot.floor)
  let y = top
  while (y - bottom > 1e-4) {
    const rest = y - bottom
    const dash = rest <= dashLen + gap ? rest : dashLen
    const next = y - dash
    appendDropDash(positions, indices, foot.x, foot.z, next, y, half)
    y = next - gap
  }
}

export function displayedHandleTangent(stored: PathVec3 | null, shown: PathVec3, scale: number): PathVec3 {
  if (stored && vecLen(stored) > 1e-8) return shown
  const len = vecLen(shown)
  const cap = scale * 1.6
  if (len <= cap || len < 1e-8) return shown
  const k = cap / len
  return [shown[0] * k, shown[1] * k, shown[2] * k]
}

function cssColorToHex(color: string): string {
  const match = color.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/)
  if (!match) return "#000000"
  const channel = (value: string) =>
    Math.max(0, Math.min(255, Math.round(Number(value))))
      .toString(16)
      .padStart(2, "0")
  return `#${channel(match[1])}${channel(match[2])}${channel(match[3])}`
}

function readTokenHex(token: string): string {
  if (typeof document === "undefined") return "#000000"
  const probe = document.createElement("span")
  probe.style.backgroundColor = `var(${token})`
  document.documentElement.appendChild(probe)
  const color = getComputedStyle(probe).backgroundColor
  probe.remove()
  return cssColorToHex(color)
}

function pathColors(theme: SceneThemeId): PathColors {
  const tokens = PATH_COLOR_TOKENS[theme]
  return {
    curve: readTokenHex(tokens.curve),
    handle: readTokenHex(tokens.handle),
    drop: readTokenHex(tokens.drop),
    frustum: readTokenHex(tokens.frustum),
    body: readTokenHex(tokens.body),
  }
}

/** Same accent as the trajectory curve: orange in light, purple in dark. */
export function pathCurveHex(theme: SceneThemeId): string {
  return pathColors(theme).curve
}

function densifyPolyline(poly: CurveSample[]): PathVec3[] {
  if (poly.length === 0) return []
  const positions: PathVec3[] = [poly[0].pos]
  for (let i = 1; i < poly.length; i++) {
    const a = poly[i - 1].pos
    const b = poly[i].pos
    const len = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])
    if (!Number.isFinite(len)) break
    const steps = Math.max(1, Math.ceil(len / MAX_CURVE_SEGMENT))
    if (steps > MAX_DENSIFY_STEPS) break
    for (let step = 1; step <= steps; step++) {
      const t = step / steps
      positions.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t])
    }
  }
  return positions
}

function cross(a: PathVec3, b: PathVec3): PathVec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

function unitVec(v: PathVec3): PathVec3 {
  const len = vecLen(v)
  if (len < 1e-8) return [0, 1, 0]
  return [v[0] / len, v[1] / len, v[2] / len]
}

function tubeBasis(tangent: PathVec3, prevSide: PathVec3 | null): { side: PathVec3; up: PathVec3 } {
  const t = unitVec(tangent)
  let side: PathVec3 | null = null
  if (prevSide) {
    const along = prevSide[0] * t[0] + prevSide[1] * t[1] + prevSide[2] * t[2]
    side = [prevSide[0] - t[0] * along, prevSide[1] - t[1] * along, prevSide[2] - t[2] * along]
    if (vecLen(side) < 1e-6) side = null
  }
  if (!side) {
    const helper: PathVec3 = Math.abs(t[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]
    side = cross(helper, t)
  }
  side = unitVec(side)
  return { side, up: unitVec(cross(t, side)) }
}

function basicMat(color: string, opacity: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    // Сетка пола прозрачная и рисуется после непрозрачных. Полная непрозрачность всё равно в этой очереди.
    transparent: true,
    opacity,
    depthTest: true,
    depthWrite: true,
  })
}

function hitMat(): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
    colorWrite: false,
  })
}

function noRaycast(obj: THREE.Object3D) {
  obj.raycast = () => {}
  obj.traverse((child) => {
    child.raycast = () => {}
  })
}

function indexCanReuse(index: THREE.BufferAttribute, indices: ArrayLike<number>, vertexCount: number): boolean {
  if (index.count !== indices.length) return false
  if (index.array instanceof Uint32Array) return true
  return vertexCount <= 65536
}

function writeBuffers(geometry: THREE.BufferGeometry, positions: ArrayLike<number>, indices?: ArrayLike<number>) {
  const position = geometry.getAttribute("position") as THREE.BufferAttribute | undefined
  const index = geometry.getIndex()
  const samePositions = position != null && position.array.length === positions.length
  const sameIndex = indices == null || (index != null && indexCanReuse(index, indices, positions.length / 3))
  if (samePositions && sameIndex && position) {
    position.array.set(positions)
    position.needsUpdate = true
    if (indices != null && index) {
      index.array.set(indices)
      index.needsUpdate = true
    }
  } else {
    // dispose() атрибута в WebGL буфер не снимает. Геометрию освобождаем, пока старые атрибуты ещё на ней.
    position?.dispose()
    if (indices != null) index?.dispose()
    geometry.dispose()
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
    if (indices != null) geometry.setIndex(Array.from(indices))
  }
  geometry.computeBoundingSphere()
}

function setPositions(geometry: THREE.BufferGeometry, positions: number[]) {
  writeBuffers(geometry, positions)
}

function liftAbove(pos: PathVec3, surfaceY?: (x: number, z: number) => number): PathVec3 {
  if (!surfaceY) return pos
  const minY = surfaceY(pos[0], pos[2])
  if (!Number.isFinite(minY) || pos[1] >= minY) return pos
  return [pos[0], minY, pos[2]]
}

function sub(a: PathVec3, b: PathVec3): PathVec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

function markerDistance(camera: PathVec3, pos: PathVec3): number {
  return Math.hypot(camera[0] - pos[0], camera[1] - pos[1], camera[2] - pos[2])
}

function farFromCamera(camera: PathVec3, pos: PathVec3, near: number): boolean {
  return markerDistance(camera, pos) >= near
}

function farRuns(samples: PathVec3[], camera: PathVec3, near: number): PathVec3[][] {
  const runs: PathVec3[][] = []
  let run: PathVec3[] = []
  const flush = () => {
    if (run.length >= 2) runs.push(run)
    run = []
  }
  for (const point of samples) {
    if (!farFromCamera(camera, point, near)) {
      flush()
      continue
    }
    run.push(point)
  }
  flush()
  return runs
}

function appendTube(positions: number[], indices: number[], samples: PathVec3[], radius: number) {
  if (samples.length < 2) return
  const sides = TUBE_SIDES
  const base = positions.length / 3
  let prevSide: PathVec3 | null = null
  for (let i = 0; i < samples.length; i++) {
    const point = samples[i]
    const ahead = samples[Math.min(samples.length - 1, i + 1)]
    const behind = samples[Math.max(0, i - 1)]
    let dir = sub(ahead, behind)
    if (vecLen(dir) < 1e-6) dir = [0, 0, 1]
    const basis = tubeBasis(dir, prevSide)
    prevSide = basis.side
    for (let k = 0; k < sides; k++) {
      const angle = (k / sides) * Math.PI * 2
      const cos = Math.cos(angle)
      const sin = Math.sin(angle)
      positions.push(
        point[0] + (basis.side[0] * cos + basis.up[0] * sin) * radius,
        point[1] + (basis.side[1] * cos + basis.up[1] * sin) * radius,
        point[2] + (basis.side[2] * cos + basis.up[2] * sin) * radius,
      )
    }
    if (i + 1 < samples.length) {
      const ring = base + i * sides
      const next = ring + sides
      for (let k = 0; k < sides; k++) {
        const k1 = (k + 1) % sides
        indices.push(ring + k, next + k, ring + k1, ring + k1, next + k, next + k1)
      }
    }
  }
  const startCap = positions.length / 3
  positions.push(samples[0][0], samples[0][1], samples[0][2])
  const endCap = positions.length / 3
  positions.push(samples[samples.length - 1][0], samples[samples.length - 1][1], samples[samples.length - 1][2])
  for (let k = 0; k < sides; k++) {
    const k1 = (k + 1) % sides
    indices.push(startCap, base + k1, base + k)
    const end = base + (samples.length - 1) * sides
    indices.push(endCap, end + k, end + k1)
  }
}

function hitRadius(visualRadius: number, distance: number): number {
  return Math.max(visualRadius * 2.5, distance * 0.02)
}

function recordedPolyline(path: CameraPath): CurveSample[] {
  const samples = path.samples
  if (samples.length < 2) return []
  const step = Math.max(1, Math.floor(samples.length / 240))
  const out: CurveSample[] = []
  for (let i = 0; i < samples.length; i += step) {
    const next = samples[Math.min(samples.length - 1, i + step)]
    out.push({ pos: samples[i].pos, dir: sub(next.pos, samples[i].pos), segment: 0 })
  }
  const last = samples[samples.length - 1]
  const tail = out[out.length - 1]
  if (!tail || tail.pos[0] !== last.pos[0] || tail.pos[1] !== last.pos[1] || tail.pos[2] !== last.pos[2]) {
    const prev = samples[Math.max(0, samples.length - 1 - step)]
    out.push({ pos: last.pos, dir: sub(last.pos, prev.pos), segment: 0 })
  }
  return out
}

function polylineOf(path: CameraPath): CurveSample[] {
  if (path.authoring && path.authoring.points.length >= 2) return curvePolyline(path.authoring)
  return recordedPolyline(path)
}

function createCameraGizmo(
  edgeColor: string,
  bodyColor: string,
): {
  group: THREE.Group
  edgeMaterial: THREE.MeshStandardMaterial
  bodyMaterial: THREE.MeshStandardMaterial
} {
  const group = new THREE.Group()
  group.frustumCulled = false
  // Вложенная группа сбрасывает порядок родителя. 0 уходит под пол и сетку.
  // 2 — та же очередь, что у линии траектории. Шар лампы (группа 3) рисуется следом
  // и закрывает рупор: сам шар глубину не пишет, поэтому порядок 4 кладёт камеру поверх него.
  group.renderOrder = 2
  const edgeMaterial = new THREE.MeshStandardMaterial({
    color: edgeColor,
    emissive: edgeColor,
    emissiveIntensity: 0.3,
    roughness: 0.5,
    transparent: true,
    opacity: 1,
    depthTest: true,
    depthWrite: true,
  })
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: bodyColor,
    roughness: 0.55,
    metalness: 0.15,
    transparent: true,
    opacity: 1,
    depthTest: true,
    depthWrite: true,
  })
  const stick = (a: THREE.Vector3, b: THREE.Vector3) => {
    const d = new THREE.Vector3().subVectors(b, a)
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, d.length(), 12), edgeMaterial)
    mesh.position.copy(a).addScaledVector(d, 0.5)
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize())
    mesh.frustumCulled = false
    return mesh
  }
  const apex = new THREE.Vector3(0, 0, 0)
  const depth = 1
  const width = 1.4
  const height = 0.9
  const corners = [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([x, y]) => new THREE.Vector3((x * width) / 2, (y * height) / 2, -depth))
  corners.forEach((corner, index) => {
    group.add(stick(apex, corner))
    group.add(stick(corner, corners[(index + 1) % corners.length]))
  })
  const bodyScale = 1 / 1.5
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.55 * bodyScale, 0.32 * bodyScale, 0.7 * bodyScale), bodyMaterial)
  body.position.z = 0.35 * bodyScale
  body.frustumCulled = false
  group.add(body)
  return { group, edgeMaterial, bodyMaterial }
}

export class CameraPathOverlay {
  readonly group = new THREE.Group()

  private readonly curve: THREE.Mesh
  private readonly curveMaterial: THREE.MeshBasicMaterial
  private readonly dropDashMaterial: THREE.MeshBasicMaterial
  private readonly dropDashes: THREE.Mesh
  private readonly iconEdgeMaterial: THREE.MeshStandardMaterial
  private readonly iconBodyMaterial: THREE.MeshStandardMaterial
  private readonly points: THREE.Mesh[] = []
  private readonly pointHits: THREE.Mesh[] = []
  private readonly start: THREE.Mesh
  private readonly endRing: THREE.Mesh
  private readonly dropRings: THREE.Mesh[] = []
  private readonly dropRingGeo = new THREE.TorusGeometry(0.22, 0.015, 6, 20)
  private readonly icon: THREE.Group
  private readonly iconLook = new THREE.Vector3()
  private readonly iconRight = new THREE.Vector3(1, 0, 0)
  private readonly iconUp = new THREE.Vector3()
  private readonly iconX = new THREE.Vector3()
  private readonly iconY = new THREE.Vector3()
  private readonly iconZ = new THREE.Vector3()
  private readonly iconBasis = new THREE.Matrix4()
  private readonly handles: THREE.Mesh[]
  private readonly handleHits: THREE.Mesh[]
  private readonly handleLine: THREE.Line
  private readonly hoverRing: THREE.Mesh
  private readonly ghostMaterial: THREE.LineBasicMaterial
  private readonly ghostLines: THREE.Line[] = []
  private readonly pointHitGeo = new THREE.SphereGeometry(1, 12, 8)
  private colorKey = ""
  private colors: PathColors = pathColors("light")

  constructor() {
    this.group.frustumCulled = false
    this.group.renderOrder = 2

    this.curveMaterial = new THREE.MeshBasicMaterial({
      color: this.colors.curve,
      depthTest: true,
      depthWrite: true,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
    })
    this.curve = new THREE.Mesh(new THREE.BufferGeometry(), this.curveMaterial)
    this.curve.frustumCulled = false
    this.curve.renderOrder = 2
    noRaycast(this.curve)
    this.group.add(this.curve)

    this.dropDashMaterial = new THREE.MeshBasicMaterial({
      color: this.colors.drop,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      side: THREE.DoubleSide,
    })
    this.dropDashes = new THREE.Mesh(new THREE.BufferGeometry(), this.dropDashMaterial)
    this.dropDashes.frustumCulled = false
    this.dropDashes.renderOrder = 2
    noRaycast(this.dropDashes)
    this.group.add(this.dropDashes)

    this.start = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 8), basicMat(this.colors.curve, 1))
    this.endRing = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.02, 8, 24), basicMat(this.colors.curve, 1))
    this.endRing.rotation.x = Math.PI / 2
    noRaycast(this.start)
    noRaycast(this.endRing)
    this.group.add(this.start, this.endRing)

    const gizmo = createCameraGizmo(this.colors.frustum, this.colors.body)
    this.icon = gizmo.group
    this.iconEdgeMaterial = gizmo.edgeMaterial
    this.iconBodyMaterial = gizmo.bodyMaterial
    noRaycast(this.icon)
    this.group.add(this.icon)

    const handleGeo = new THREE.SphereGeometry(POINT_RADIUS, 12, 8)
    this.handles = [1, -1].map((_side) => {
      const mesh = new THREE.Mesh(handleGeo, basicMat(this.colors.handle, 1))
      mesh.visible = false
      mesh.frustumCulled = false
      noRaycast(mesh)
      this.group.add(mesh)
      return mesh
    })
    this.handleHits = [1, -1].map((side) => {
      const mesh = new THREE.Mesh(this.pointHitGeo, hitMat())
      mesh.userData.pathHit = { kind: "handle", side, index: 0 }
      mesh.visible = false
      mesh.frustumCulled = false
      this.group.add(mesh)
      return mesh
    })
    this.handleLine = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ depthTest: false, transparent: true, opacity: 1 }),
    )
    noRaycast(this.handleLine)
    this.handleLine.visible = false
    this.group.add(this.handleLine)

    this.hoverRing = new THREE.Mesh(this.dropRingGeo, basicMat(this.colors.drop, 1))
    this.hoverRing.rotation.x = Math.PI / 2
    this.hoverRing.visible = false
    this.hoverRing.frustumCulled = false
    this.hoverRing.renderOrder = 4
    noRaycast(this.hoverRing)
    this.group.add(this.hoverRing)

    this.ghostMaterial = new THREE.LineBasicMaterial({
      color: this.colors.curve,
      transparent: true,
      opacity: DIM_OPACITY,
      depthTest: true,
    })
  }

  setResolution(_width: number, _height: number) {
    // Гизмо из цилиндров не зависит от разрешения кадра.
  }

  sync(input: PathOverlaySync) {
    const poly = polylineOf(input.path)
    const points = input.path.authoring?.points ?? []
    const ghosts = input.hidden ? [] : (input.ghosts ?? [])
    this.drawGhosts(ghosts)
    const ghosting = ghosts.some((line) => line.length >= 2)
    const show = !input.hidden && (poly.length >= 2 || points.length > 0 || Boolean(input.insertHover) || ghosting)
    this.group.visible = show
    if (!show) {
      this.hoverRing.visible = false
      return
    }

    const bare = Boolean(input.solid) && !input.editing
    this.applyColors(input.theme, input.editing || input.solid ? 1 : DIM_OPACITY)
    const chrome = input.chrome ?? 1
    const near = NEAR_HIDE_M * chrome
    const distance = Math.hypot(
      input.cameraPosition[0] - (poly[0]?.pos[0] ?? points[0]?.pos[0] ?? 0),
      input.cameraPosition[1] - (poly[0]?.pos[1] ?? points[0]?.pos[1] ?? 0),
      input.cameraPosition[2] - (poly[0]?.pos[2] ?? points[0]?.pos[2] ?? 0),
    )
    const chromeDistance = chrome > 1e-6 ? distance / chrome : distance
    const scale = Math.min(2, Math.max(0.2, chromeDistance * 0.045)) * chrome

    const samples = input.path.samples
    const recordedEnds =
      points.length === 0 && samples.length >= 2
        ? [{ pos: samples[0].pos }, { pos: samples[samples.length - 1].pos }]
        : null

    const markerPoints = bare ? [] : points
    const markerPoly = bare ? [] : poly
    this.drawCurve(poly, input.cameraPosition, input.surfaceY, near, TUBE_RADIUS * chrome)
    this.drawDrops(markerPoints, input.floors, scale, input.cameraPosition, near, chrome)
    this.drawEnds(markerPoly, markerPoints, scale, input.cameraPosition, near)
    this.drawIcon(bare ? null : input.icon, input.cameraPosition, near, chrome)
    this.drawPoints(
      bare ? [] : (recordedEnds ?? points),
      input.editing,
      input.selectedIndex,
      scale,
      input.cameraPosition,
      near,
      chrome,
    )
    this.drawHandles(input.path, input.editing && !bare, input.selectedIndex, scale, input.cameraPosition, near, chrome)
    this.drawInsertHover(bare ? null : input.insertHover, scale, input.surfaceY, chrome)
  }

  private drawGhosts(ghosts: PathVec3[][]) {
    while (this.ghostLines.length < ghosts.length) {
      const line = new THREE.Line(new THREE.BufferGeometry(), this.ghostMaterial)
      line.frustumCulled = false
      line.renderOrder = 1
      noRaycast(line)
      this.group.add(line)
      this.ghostLines.push(line)
    }
    this.ghostLines.forEach((line, index) => {
      const poly = ghosts[index]
      const visible = Boolean(poly && poly.length >= 2)
      line.visible = visible
      if (!poly || !visible) return
      const positions = new Float32Array(poly.length * 3)
      poly.forEach((pos, point) => {
        positions[point * 3] = pos[0]
        positions[point * 3 + 1] = pos[1]
        positions[point * 3 + 2] = pos[2]
      })
      writeBuffers(line.geometry, positions)
    })
  }

  hit(raycaster: THREE.Raycaster): PathOverlayHit | null {
    if (!this.group.visible) return null
    const hits = raycaster.intersectObjects([this.group], true)
    for (const item of hits) {
      let node: THREE.Object3D | null = item.object
      while (node) {
        const data = node.userData.pathHit as PathOverlayHit | undefined
        if (data?.kind) return data
        node = node.parent
      }
    }
    return null
  }

  dispose() {
    this.group.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      mesh.geometry?.dispose?.()
      const material = mesh.material as THREE.Material | THREE.Material[] | undefined
      if (material === this.ghostMaterial) return
      if (Array.isArray(material)) material.forEach((entry) => entry.dispose())
      else material?.dispose?.()
    })
    this.pointHitGeo.dispose()
    this.dropRingGeo.dispose()
    this.ghostMaterial.dispose()
    this.group.removeFromParent()
  }

  private applyColors(theme: SceneThemeId, opacity: number) {
    const app =
      typeof document === "undefined" ? "" : document.documentElement.classList.contains("light") ? "light" : "dark"
    const key = `${theme}:${opacity}:${app}`
    if (key === this.colorKey) return
    this.colorKey = key
    this.colors = pathColors(theme)
    this.curveMaterial.color.set(this.colors.curve)
    this.curveMaterial.transparent = true
    this.curveMaterial.opacity = 1
    this.dropDashMaterial.color.set(this.colors.drop)
    this.dropDashMaterial.transparent = true
    this.dropDashMaterial.opacity = opacity
    this.paint([this.start, this.endRing], this.colors.curve, opacity)
    this.paint(this.dropRings, this.colors.drop, opacity)
    this.paint([this.hoverRing], this.colors.drop, opacity)
    this.iconEdgeMaterial.color.set(this.colors.frustum)
    this.iconEdgeMaterial.emissive.set(this.colors.frustum)
    this.iconBodyMaterial.color.set(this.colors.body)
    this.paint(this.handles, this.colors.handle, 1)
    this.ghostMaterial.color.set(this.colors.curve)
    this.ghostMaterial.transparent = true
    this.ghostMaterial.opacity = DIM_OPACITY
    const handleMat = this.handleLine.material as THREE.LineBasicMaterial
    handleMat.color.set(this.colors.handle)
    handleMat.transparent = true
    handleMat.opacity = 1
  }

  private paint(meshes: THREE.Mesh[], color: string, opacity: number) {
    for (const mesh of meshes) {
      const mat = mesh.material as THREE.MeshBasicMaterial
      mat.color.set(color)
      mat.opacity = opacity
      mat.transparent = true
    }
  }

  private drawCurve(
    poly: CurveSample[],
    camera: PathVec3,
    surfaceY: ((x: number, z: number) => number) | undefined,
    near: number,
    radius: number,
  ) {
    const samples = densifyPolyline(poly).map((point) => liftAbove(point, surfaceY))
    const positions: number[] = []
    const indices: number[] = []
    for (const run of farRuns(samples, camera, near)) appendTube(positions, indices, run, radius)
    this.curve.visible = indices.length > 0
    writeBuffers(this.curve.geometry, positions, indices)
  }

  private drawDrops(
    points: { pos: PathVec3 }[],
    floors: Array<number | null>,
    scale: number,
    camera: PathVec3,
    near: number,
    chrome: number,
  ) {
    const feet: Array<{ x: number; y: number; z: number; floor: number }> = []
    points.forEach((point, index) => {
      const floor = floors[index]
      if (floor == null || !Number.isFinite(floor)) return
      if (Math.abs(point.pos[1] - floor) < FLOOR_CLEARANCE) return
      if (!farFromCamera(camera, point.pos, near)) return
      feet.push({ x: point.pos[0], y: point.pos[1], z: point.pos[2], floor })
    })
    const positions: number[] = []
    const indices: number[] = []
    const dash = DROP_DASH * chrome
    const gap = DROP_GAP * chrome
    const half = DROP_HALF * chrome
    for (const foot of feet) appendDropDashes(positions, indices, foot, dash, gap, half)
    const geometry = this.dropDashes.geometry
    writeBuffers(geometry, positions, indices)
    this.dropDashes.visible = feet.length > 0
    while (this.dropRings.length < feet.length) {
      const mesh = new THREE.Mesh(this.dropRingGeo, basicMat(this.colors.drop, 1))
      mesh.rotation.x = Math.PI / 2
      mesh.frustumCulled = false
      noRaycast(mesh)
      this.group.add(mesh)
      this.dropRings.push(mesh)
    }
    this.dropRings.forEach((mesh, index) => {
      const foot = feet[index]
      mesh.visible = Boolean(foot)
      if (!foot) return
      mesh.position.set(foot.x, foot.floor + FLOOR_CLEARANCE * chrome, foot.z)
      mesh.scale.setScalar(scale)
    })
    this.paint(this.dropRings, this.colors.drop, this.dropDashMaterial.opacity)
  }

  private drawEnds(poly: CurveSample[], points: { pos: PathVec3 }[], scale: number, camera: PathVec3, near: number) {
    const startPos = points[0]?.pos ?? poly[0]?.pos
    const endPos = points.length > 0 ? points[points.length - 1].pos : poly[poly.length - 1]?.pos
    this.start.visible = Boolean(startPos && farFromCamera(camera, startPos, near))
    this.endRing.visible =
      Boolean(endPos && farFromCamera(camera, endPos, near)) && (points.length >= 2 || poly.length >= 2)
    if (startPos) {
      this.start.position.set(startPos[0], startPos[1], startPos[2])
      this.start.scale.setScalar(scale)
    }
    if (endPos && this.endRing.visible) {
      this.endRing.position.set(endPos[0], endPos[1], endPos[2])
      this.endRing.scale.setScalar(scale)
    }
  }

  private drawInsertHover(
    pos: PathVec3 | null | undefined,
    scale: number,
    surfaceY: ((x: number, z: number) => number) | undefined,
    chrome: number,
  ) {
    this.hoverRing.visible = Boolean(pos)
    if (!pos) return
    const floor = surfaceY?.(pos[0], pos[2]) ?? 0
    this.hoverRing.position.set(pos[0], floor + FLOOR_CLEARANCE * chrome, pos[2])
    this.hoverRing.rotation.x = Math.PI / 2
    this.hoverRing.scale.setScalar(scale)
  }

  private drawIcon(icon: { pos: PathVec3; dir: PathVec3 } | null, camera: PathVec3, near: number, chrome: number) {
    const far = icon != null && farFromCamera(camera, icon.pos, near)
    this.icon.visible = Boolean(icon) && far && vecLen(icon?.dir ?? [0, 0, 0]) > 1e-4
    if (!icon || !this.icon.visible) return
    this.icon.position.set(icon.pos[0], icon.pos[1], icon.pos[2])
    this.icon.scale.setScalar(ICON_SCALE * chrome)
    this.iconLook.set(icon.dir[0], icon.dir[1], icon.dir[2])
    if (this.iconLook.lengthSq() < 1e-8) return
    this.iconLook.normalize()
    this.iconZ.copy(this.iconLook).negate()
    this.iconUp.set(0, 1, 0)
    this.iconX.crossVectors(this.iconUp, this.iconZ)
    if (this.iconX.lengthSq() < 1e-8) {
      this.iconX.copy(this.iconRight)
      this.iconX.addScaledVector(this.iconLook, -this.iconX.dot(this.iconLook))
      if (this.iconX.lengthSq() < 1e-8) this.iconX.set(1, 0, 0)
    }
    this.iconX.normalize()
    this.iconY.crossVectors(this.iconZ, this.iconX).normalize()
    this.iconBasis.makeBasis(this.iconX, this.iconY, this.iconZ)
    this.icon.quaternion.setFromRotationMatrix(this.iconBasis)
    this.iconRight.copy(this.iconX)
  }

  private drawPoints(
    points: { pos: PathVec3 }[],
    editing: boolean,
    selected: number | null,
    _scale: number,
    camera: PathVec3,
    near: number,
    chrome: number,
  ) {
    const pointGeo = this.points[0]?.geometry ?? new THREE.SphereGeometry(POINT_RADIUS, 10, 8)
    while (this.points.length < points.length) {
      const mesh = new THREE.Mesh(pointGeo, basicMat(this.colors.curve, 1))
      mesh.frustumCulled = false
      mesh.renderOrder = 3
      noRaycast(mesh)
      const hit = new THREE.Mesh(this.pointHitGeo, hitMat())
      hit.frustumCulled = false
      hit.userData.pathHit = { kind: "point", index: this.points.length }
      this.group.add(mesh, hit)
      this.points.push(mesh)
      this.pointHits.push(hit)
    }
    this.points.forEach((mesh, index) => {
      const point = points[index]
      const hit = this.pointHits[index]
      mesh.visible = Boolean(point)
      if (!point) {
        hit.visible = false
        hit.raycast = () => {}
        return
      }
      const selectedPoint = editing && index === selected
      const mat = mesh.material as THREE.MeshBasicMaterial
      mat.color.set(selectedPoint ? this.colors.handle : this.colors.curve)
      mat.opacity = 1
      mat.transparent = true
      mesh.position.set(point.pos[0], point.pos[1], point.pos[2])
      mesh.scale.setScalar(chrome * 2)
      const show = farFromCamera(camera, point.pos, near)
      mesh.visible = show
      hit.visible = show
      hit.raycast = show ? THREE.Mesh.prototype.raycast : () => {}
      if (!show) return
      hit.position.copy(mesh.position)
      hit.scale.setScalar(POINT_RADIUS * chrome * 2)
      hit.userData.pathHit = { kind: "point", index }
    })
  }

  private drawHandles(
    path: CameraPath,
    editing: boolean,
    selected: number | null,
    scale: number,
    camera: PathVec3,
    near: number,
    chrome: number,
  ) {
    const authoring = path.authoring
    const point = selected != null ? authoring?.points[selected] : undefined
    const show = Boolean(editing && authoring?.smooth && point)
    this.handleLine.visible = show
    if (!authoring || !point || selected == null || !show) {
      this.handles.forEach((mesh) => {
        mesh.visible = false
      })
      this.handleHits.forEach((mesh) => {
        mesh.visible = false
      })
      return
    }
    const tangent = displayedHandleTangent(point.tangent, displayedTangent(authoring, selected), scale)
    const ends: Array<{ pos: PathVec3; side: 1 | -1 }> = [
      {
        pos: [point.pos[0] + tangent[0], point.pos[1] + tangent[1], point.pos[2] + tangent[2]],
        side: 1,
      },
      {
        pos: [point.pos[0] - tangent[0], point.pos[1] - tangent[1], point.pos[2] - tangent[2]],
        side: -1,
      },
    ]
    ends.forEach((end, index) => {
      const mesh = this.handles[index]
      const hit = this.handleHits[index]
      const mat = mesh.material as THREE.MeshBasicMaterial
      mat.opacity = 1
      mat.transparent = true
      const show = farFromCamera(camera, end.pos, near)
      mesh.visible = show
      hit.visible = show
      const handleScale = Math.min(scale, chrome)
      mesh.position.set(end.pos[0], end.pos[1], end.pos[2])
      mesh.scale.setScalar(handleScale)
      hit.position.copy(mesh.position)
      hit.scale.setScalar(hitRadius(POINT_RADIUS * handleScale, markerDistance(camera, end.pos)))
      hit.userData.pathHit = { kind: "handle", index: selected, side: end.side }
    })
    const [a, b] = ends
    const lineVisible = farFromCamera(camera, a.pos, near) && farFromCamera(camera, b.pos, near)
    this.handleLine.visible = lineVisible
    if (lineVisible) setPositions(this.handleLine.geometry, [...a.pos, ...b.pos])
  }
}
